import React, { useCallback, useEffect, useRef } from 'react'
import { SpaceCard } from '..'
import { ISpace, IEdge, IPageInfo } from '@api/types'
import api, { ISpaceType } from '@api'

import GenericSelectWithInfiniteScroll, {
  SelectProps,
  ItemProps,
} from './GenericSelectWithInfiniteScroll'
import { SpaceCardSkeleton, SpaceIcon } from '@ui/SpaceCard'
import Flex from '@ui/Flex'
import { seatable } from '@ui/canvas/utils'

interface SpaceSelectProps extends SelectProps<ISpace> {
  floorId?: string
  showSeatableAndAllocatable?: boolean
  usageTypeIds?: string[]
  onclose?: boolean
}

let persistedSTState: ISpaceType[] = []

function useSpaceTypes(): ISpaceType[] {
  //this ref is used to prevent memory leakage.
  const isUnmountedRef = useRef(false)

  const [spaceTypes, setSpaceTypes] = React.useState<ISpaceType[]>(persistedSTState)

  useEffect(() => {
    api.company.spaceTypes().then((spaceTypes) => {
      if (!isUnmountedRef.current) setSpaceTypes(spaceTypes)
    })

    return () => {
      isUnmountedRef.current = true
    }
  }, [isUnmountedRef])

  useEffect(() => {
    // Cache initial list
    if (!persistedSTState.length && spaceTypes.length) {
      persistedSTState = spaceTypes
    }
  })

  return spaceTypes
}

export default function SpaceSelectWrapper(props: SpaceSelectProps) {
  const spaceTypes = useSpaceTypes()

  const search = useCallback(
    (listProps: { query: string; after?: string }) => {
      if (props.showSeatableAndAllocatable) {
        if (spaceTypes.length) {
          return api.space.search({
            ...listProps,
            usageTypeIds: props.usageTypeIds,
            floorId: props.floorId,
            ...(props.showSeatableAndAllocatable
              ? {
                  spaceTypeIds: spaceTypes
                    .filter((st) => st.interactivityType === 1 || st.interactivityType === 2)
                    .map((s) => s.id),
                }
              : {}),
          })
        } else {
          return new Promise<{ pageInfo: IPageInfo; edges: IEdge<ISpace>[] }>((resolve) => {
            resolve({
              pageInfo: {
                endCursor: '',
                hasNextPage: false,
              },
              edges: [],
            })
          })
        }
      }

      return api.space.search({
        ...listProps,
        usageTypeIds: props.usageTypeIds,
        floorId: props.floorId,
      })
    },
    [props, spaceTypes]
  )

  const handleClose = (hook: any) => {
    hook.reset()
  }

  return (
    <GenericSelectWithInfiniteScroll<ISpace>
      {...props}
      list={search}
      Item={SpaceItem}
      onClose={props.onclose ? (event) => handleClose(event) : () => {}}
    />
  )
}

export function SpaceItem({ type, data, refItem, query }: ItemProps<ISpace>) {
  if (type === 'skeleton') {
    return <SpaceCardSkeleton />
  }

  if (type === 'value') {
    return (
      <Flex align="center">
        <SpaceIcon space={data as ISpace} />
        <span style={{ marginLeft: 10 }}>
          {seatable((data as ISpace).spaceType) ? data?.externalId : data?.name || data?.externalId}
        </span>
      </Flex>
    )
  }

  return (
    <div ref={refItem}>
      <SpaceCard space={data as ISpace} query={query} />
    </div>
  )
}
